import { useCallback } from 'react';
import { Thread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { Options } from '@frontend/fetch';
import { useSchemaQueryUpdaters } from '@frontend/react-query-helpers';
import { useFetchGetThreadDetails } from '../../queries';
import { serviceName } from '../../service';
import { ServiceQueries } from '../../types';
import { ResourceMatchingOptions } from '../types';
import { findCachedThreadInListThreads } from './cache-resource-getters';

type UseGetThreadDetailsArgs = {
  httpOptions?: Options;
};

export const useGetThreadDetails = ({ httpOptions }: UseGetThreadDetailsArgs = {}) => {
  const queryUpdaters = useSchemaQueryUpdaters<ServiceQueries>(serviceName);
  const fetchGetThreadDetails = useFetchGetThreadDetails();

  const getThreadDetails = useCallback(
    async ({
      threadId,
      locationId,
      excludeLastAutomated,
      cacheOnly = false,
      matchStrategy,
      matchFn,
      httpOptions: customHttpOptions,
    }: {
      threadId: string;
      locationId: string;
      excludeLastAutomated: boolean;
      cacheOnly?: boolean;
      httpOptions?: Options;
    } & Partial<Pick<ResourceMatchingOptions<Thread>, 'matchStrategy' | 'matchFn'>>) => {
      const listThreadsCachesToSearch = queryUpdaters.getQueriesData<'ListThreads', true>({
        endpointName: 'ListThreads',
      });

      let cachedThreadFromListThreads: Thread | undefined;
      if (excludeLastAutomated) {
        // If we are excluding the last automated message one of the following must be true:
        // - Find a matching thread on a request with the same filter
        // - Find a matching thread with a manual message
        const { matchingFilterQueries, otherQueries } = listThreadsCachesToSearch.reduce<{
          matchingFilterQueries: (typeof listThreadsCachesToSearch)[number][1][];
          otherQueries: (typeof listThreadsCachesToSearch)[number][1][];
        }>(
          (acc, [queryKey, data]) => {
            const requestMatchesAutomatedMessageFilter =
              !!queryKey[2].filter?.excludeAutomatedLastMessage === !!excludeLastAutomated;
            if (requestMatchesAutomatedMessageFilter) {
              acc.matchingFilterQueries.push(data);
            } else {
              acc.otherQueries.push(data);
            }
            return acc;
          },
          { matchingFilterQueries: [], otherQueries: [] }
        );
        cachedThreadFromListThreads =
          findCachedThreadInListThreads(matchingFilterQueries, {
            matchValues: { threadId, locationId },
            matchStrategy: matchStrategy ?? 'or',
            matchFn,
          }) ??
          findCachedThreadInListThreads(otherQueries, {
            matchValues: { threadId, locationId },
            matchStrategy: 'and',
            matchFn: (thread) => {
              const messageIsManual = !thread.messages[0]?.autogeneratedBy;
              return messageIsManual && (matchFn?.(thread) ?? true);
            },
          });
      } else {
        cachedThreadFromListThreads = findCachedThreadInListThreads(
          listThreadsCachesToSearch.map(([, data]) => data),
          { matchValues: { threadId, locationId }, matchStrategy: 'or' }
        );
      }

      if (cachedThreadFromListThreads) {
        return cachedThreadFromListThreads;
      }

      if (cacheOnly) {
        const cachedThreadDetails = queryUpdaters.getQueriesData({
          endpointName: 'GetThreadDetails',
          queryFilters: {
            queryKey: queryUpdaters.getQueryKey<'GetThreadDetails'>({
              endpointName: 'GetThreadDetails',
              request: {
                threadId,
                locationId,
                excludeAutomatedLastMessage: excludeLastAutomated ? true : undefined,
              },
            }),
          },
        });
        const thread = cachedThreadDetails[0]?.[1]?.thread;
        if (matchStrategy === 'and' && matchFn && thread && !matchFn(thread)) return undefined;
        return thread;
      } else {
        try {
          const result =
            threadId && locationId
              ? await fetchGetThreadDetails({
                  request: {
                    threadId,
                    locationId,
                    excludeAutomatedLastMessage: excludeLastAutomated ? true : undefined,
                  },
                  options: {
                    retryDelay: 500,
                    retry: 2,
                  },
                  httpOptions: { ...httpOptions, ...customHttpOptions },
                })
              : undefined;

          return result?.thread.id ? result.thread : undefined;
        } catch (error) {
          return undefined;
        }
      }
    },
    [fetchGetThreadDetails, httpOptions, queryUpdaters]
  );

  return getThreadDetails;
};
